// src/modules/fire/Quotation/utils/normalizeCoverage.ts

export interface NormalizedCoverage {
	id: string; // ID interno consistente (sempre o UUID da cobertura)
	coverageId: string; // mesmo que id acima (chave de correlação)

	name: string;

	coverage_value?: number;
	coverage_prizeTotal?: number;

	limite_min?: number;
	limite_max?: number;

	factor?: number | null;
	rate_increase?: number | string | null;

	integrator_code?: string;

	// load = vindo de cotação salva / metadata inicial
	// calc = vindo do endpoint de cálculo de prêmio
	source: "load" | "calc";

	enabled?: boolean;
	is_changed_by_user?: boolean;
}

// helper simples para detectar UUID (bem suficiente para o seu caso)
function isUuidLike(value: any): value is string {
	if (typeof value !== "string") return false;
	return value.includes("-") && value.length >= 10;
}

/**
 * Normaliza QUALQUER um dos três formatos de cobertura:
 *
 * 1) Load de cotação completa (edição)
 *    - tem eventsId
 *    - coverageId = UUID da cobertura
 *    - id = id do item da cotação
 *
 * 2) Resultado do endpoint de cálculo de valores
 *    - coverageId = UUID da cobertura
 *    - não tem id, nem name, nem factor
 *
 * 3) Endpoint de carregamento de coberturas (metadata base)
 *    - id = UUID da cobertura
 *    - coverageId = "1", "2", ...
 *    - tem name, factor, limite_min/limite_max (string)
 */
export function normalizeCoverage(raw: any): NormalizedCoverage {
	// ---------------------------------------------------------
	// 1. Descobrir o "coverage UUID" canônico (chave de correlação)
	// ---------------------------------------------------------
	const rawCoverageId = raw?.coverageId?.toString?.();
	const rawId = raw?.id?.toString?.();

	let canonicalId: string;

	if (isUuidLike(rawCoverageId)) {
		// casos 1 e 2: coverageId já é o UUID da cobertura
		canonicalId = rawCoverageId;
	} else if (isUuidLike(rawId)) {
		// caso 3 (metadata): id é o UUID da cobertura
		canonicalId = rawId;
	} else {
		// fallback ultra-defensivo (não deveria acontecer nesses 3 cenários)
		canonicalId = rawCoverageId || rawId || "";
	}

	// ---------------------------------------------------------
	// 2. Detectar a "origem" principal
	// ---------------------------------------------------------
	const hasCalcValues = raw.coverage_value !== undefined || raw.coverage_prizeTotal !== undefined;
	const hasEventsId = raw.eventsId !== undefined;

	// regra:
	// - se veio de cotação salva (tem eventsId) → "load"
	// - senão, se tem valores de cálculo → "calc"
	// - senão → tratamos como "load" (metadata base)
	const source: "load" | "calc" = hasEventsId ? "load" : hasCalcValues ? "calc" : "load";

	// ---------------------------------------------------------
	// 3. Normalizar campos numéricos
	// ---------------------------------------------------------
	const coverage_value =
		raw.coverage_value !== undefined && raw.coverage_value !== null
			? Number(raw.coverage_value)
			: undefined;

	const coverage_prizeTotal =
		raw.coverage_prizeTotal !== undefined && raw.coverage_prizeTotal !== null
			? Number(raw.coverage_prizeTotal)
			: undefined;

	const limite_min =
		raw.limite_min !== undefined && raw.limite_min !== null
			? Number(raw.limite_min)
			: undefined;

	const limite_max =
		raw.limite_max !== undefined && raw.limite_max !== null
			? Number(raw.limite_max)
			: undefined;

	const factor =
		raw.factor !== undefined && raw.factor !== null
			? Number(raw.factor)
			: null;

	const rate_increase = raw.rate_increase ?? null;

	const integrator_code = raw.integrator_code ?? "";


	const name = raw.name ?? "";

	return {
		// sempre usamos o UUID da cobertura como "id" interno
		id: canonicalId,
		coverageId: canonicalId,

		name,

		coverage_value,
		coverage_prizeTotal,

		limite_min,
		limite_max,

		factor,
		rate_increase,

		integrator_code,

		source,
		enabled: raw.enabled ?? true, // Default to true if undefined, logic will refine this later
	};
}
